import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const PRODUITS = [
  { slug: "t1", name: "T1", path: "/produits/t1" },
  { slug: "t2", name: "T2", path: "/produits/t2" },
  { slug: "t3", name: "T3", path: "/produits/t3" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []); // setScrolled is stable from useState

  const navLinkClass = "font-body text-base tracking-wide text-soft-black hover:text-natural-wood transition-colors duration-200";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-off-white/90 border-b border-warm-grey/20"
          : "backdrop-blur-md bg-off-white/60"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/logo.png" alt="YONO" className="h-20 md:h-28 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 mr-6">
          <Link to="/" data-testid="nav-accueil" className={navLinkClass}>
            Accueil
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-produits" className={`${navLinkClass} flex items-center gap-1 outline-none`}>
              Produits <ChevronDown className="h-3 w-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="bg-off-white border border-warm-grey/30 rounded-none shadow-none min-w-[160px]">
              {PRODUITS.map((item) => (
                <DropdownMenuItem
                  key={item.slug}
                  data-testid={`produits-${item.slug}`}
                  onClick={() => navigate(item.path)}
                  className="font-body text-sm cursor-pointer rounded-none hover:bg-light-beige focus:bg-light-beige"
                >
                  {item.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/about" data-testid="nav-about" className={navLinkClass}>
            À propos
          </Link>
          <Link to="/contact" data-testid="nav-contact" className={navLinkClass}>
            Contact
          </Link>
        </nav>

        {/* Desktop CTA */}
        <Link
          to="/produits"
          data-testid="nav-commander-btn"
          className="hidden md:inline-block bg-soft-black text-off-white px-6 py-2.5 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300"
        >
          Découvrir la gamme
        </Link>

        {/* Mobile Menu */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger data-testid="mobile-menu-trigger" className="md:hidden p-2">
            <Menu className="h-5 w-5 text-soft-black" />
          </SheetTrigger>
          <SheetContent side="right" className="bg-off-white border-l border-warm-grey/20 w-72 p-0">
            <div className="flex flex-col h-full pt-16 px-8">
              <nav className="flex flex-col gap-6">
                <Link to="/" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-accueil" className="font-heading text-lg tracking-tight">
                  Accueil
                </Link>
                <div>
                  <Link to="/produits" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-produits" className="font-heading text-lg tracking-tight mb-3 block">
                    Produits
                  </Link>
                  <div className="flex flex-col gap-3 pl-4">
                    {PRODUITS.map((item) => (
                      <Link
                        key={item.slug}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`mobile-produits-${item.slug}`}
                        className="font-body text-sm text-soft-black/70 hover:text-soft-black"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
                <Link to="/about" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-about" className="font-heading text-lg tracking-tight">
                  À propos
                </Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-contact" className="font-heading text-lg tracking-tight">
                  Contact
                </Link>
              </nav>
              <div className="mt-auto pb-8">
                <Link
                  to="/produits"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-commander-btn"
                  className="block text-center bg-soft-black text-off-white px-6 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300"
                >
                  Découvrir la gamme
                </Link>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
